import React, { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';
import { Table, Select, Typography } from 'antd';
import 'antd/dist/reset.css'; // or 'antd/dist/antd.css'

import stance_general from './stancelabel_general.csv';
import stance_specific from './stancelabel_specific.csv';
import time_general   from './timelabel_general.csv';
import time_specific  from './timelabel_specific.csv';
import certain_general  from './certainlabel_general.csv';
import certain_specific from './certainlabel_specific.csv';

const { Option } = Select;
const { Text } = Typography;

// map bank→flag (unchanged)
const FLAG_MAP = {
  BCB: true, BCRP: true, BNM: true, BSP: true,
  BanRep: true, BoC: true, BoE: true, BoI: true,
  BoJ: true, BoK: true, BdeM: true, BoT: true,
  CBCT: true, CBE: true, CBoC: true, CBR: true,
  CBRT: true, ECB: true, FOMC: true, MAS: true,
  NBP: true, PBoC: true, RBA: true, RBI: true,
  SNB: true
};

// model logos (unchanged)
const LOGO_MAP = {
  MBB: '/static/images/icons/modernbert.png',
  BB:  '/static/images/icons/google.png',
  FB:  '/static/images/icons/HKUST.png',
  RBB: '/static/images/icons/meta.svg',
  MBL: '/static/images/icons/modernbert.png',
  BL:  '/static/images/icons/google.png',
  RBL: '/static/images/icons/meta.svg',
  Gem: '/static/images/icons/google.png',
  '4o': 'https://www.svgrepo.com/show/306500/openai.svg',
  '4.1m': 'https://www.svgrepo.com/show/306500/openai.svg',
  '4.1': 'https://www.svgrepo.com/show/306500/openai.svg',
  DS:  '/static/images/icons/deepseek.svg',
  Qwen:'/static/images/icons/qwen.svg',
  FM:  '/static/images/icons/FinMA.png',
  L3:  '/static/images/icons/meta.svg',
  L4S: '/static/images/icons/meta.svg'
};

const modelKeys = Object.keys(LOGO_MAP);

// descriptions (unchanged)
const descriptionMap = {
  Stance:   "F1-Scores of models classifying sentences from each central bank with the monetary policy stance label of Hawkish/Dovish/Neutral/Irrelevant with standard deviation in the parenthesis. The best performing PLM and LLM in each row is highlighted in blue and green, respectively. The best-performing model for each country is bolded.",
  Time:     "F1-Scores across the banks for the time label of Forward-looking/Not Forward-looking with the standard deviation in brackets. Results use a single model per bank. Best PLM/LLM in each row highlighted in blue/green; The best-performing model for each country is bolded.",
  Certainty:"F1-Scores across the banks for the certainty label of Uncertain/Certain with standard deviation in brackets. Results use a single model per bank. Best PLM/LLM in each row highlighted in blue/green; The best-performing model for each country is bolded."
};

export default function StanceTable() {
  const [dataSource, setDataSource] = useState([]);
  const [averageRow, setAverageRow] = useState(null);
  const [labelType, setLabelType]   = useState('Stance');
  const [scope, setScope]           = useState('General');

  // nested map of label → (General | Specific) → CSV
  const csvMap = {
    Stance:   { General: stance_general,  Specific: stance_specific },
    Time:     { General: time_general,    Specific: time_specific   },
    Certainty:{ General: certain_general, Specific: certain_specific}
  };

  // fetch on either labelType or scope change
  useEffect(() => {
    fetch(csvMap[labelType][scope])
      .then(r => r.text())
      .then(txt => {
        const { data } = Papa.parse(txt, {
          header: true,
          skipEmptyLines: true,
          transformHeader: h => h.trim().replace(/^\uFEFF/, ''),
          transform: v => v.trim()
        });
        const avg = data.pop();
        setAverageRow(avg);
        setDataSource(data.map((r,i) => ({ key: i, ...r })));
      });
  }, [labelType, scope]);

  // build columns (unchanged aside from flag render)
  const columns = useMemo(() => {
    const cols = [
      {
        title: 'Bank',
        dataIndex: 'Bank',
        fixed: 'left',
        width: 140,
        render: bank => {
          if (bank === 'Average') return <Text strong>Average</Text>;
          if (!FLAG_MAP[bank])     return <Text>{bank}</Text>;
          return (
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <img
                src={`/static/images/flags/${bank}.png`}
                alt={bank}
                style={{
                  marginRight: '8px',
                  height: '15px',
                  border: '1px solid #000',
                }}
              />
              <Text>{bank}</Text>
            </span>
          );
        }
      }
    ];

    modelKeys.forEach((code) => {
      cols.push({
        title: (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4
          }}>
            <img src={LOGO_MAP[code]} alt={code} style={{ height: 20 }} />
            <Text>{code}</Text>
          </div>
        ),
        dataIndex: `${code}_mean`,
      key: code,
      align: 'center',
      width: 100,
      render: (val, row) => {
        const mean = parseFloat(val) || 0;
        // find max mean across models
        const maxMean = modelKeys.reduce((m, k) => {
          const v = parseFloat(row[`${k}_mean`]) || 0;
          return v > m ? v : m;
        }, -Infinity);
        const isMax = mean === maxMean;

        // original PLM/LLM flags
        const bestPLM = row[`${code}_bestPLM`] === 'True';
        const bestLLM = row[`${code}_bestLLM`] === 'True';

        // format display
        let display = val || '';
        if (display && !display.startsWith('.')) display = `.${display}`;
        const std = row[`${code}_std`] || '';
        const stdDisplay = std && (std.startsWith('.') ? std : `.${std}`);

        return (
          <div
            style={{
              // only bold if this model has the max mean
              fontWeight: isMax ? 'bold' : undefined,
              // but always keep the original PLM/LLM coloring
              backgroundColor: bestPLM
                ? 'rgba(66,165,245,0.3)'
                : bestLLM
                ? 'rgba(129,199,132,0.3)'
                : undefined,
              borderRadius: 4,
              padding: '2px 4px'
            }}
          >
            <div>{display}</div>
            {stdDisplay && <small>({stdDisplay})</small>}
          </div>
        );
      }
    });
  });

  return cols;
}, [modelKeys]);

  return (
    <div style={{ padding: 24 }}>
      {/* description */}
      <p style={{ textAlign: 'center', marginBottom: 16 }}>
        {descriptionMap[labelType]}
      </p>

      {/* dual selectors */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 16 }}>

      <Select
          value={scope}
          onChange={setScope}
          style={{ width: 120 }}
        >
          <Option value="General">General</Option>
          <Option value="Specific">Specific</Option>
        </Select>
        <Select
          value={labelType}
          onChange={setLabelType}
          style={{ width: 220 }}
        >
          <Option value="Stance">Stance Detection</Option>
          <Option value="Time">Temporal Classification</Option>
          <Option value="Certainty">Uncertainty Estimation</Option>
        </Select>
      </div>

      {/* table */}
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        scroll={{ x: 'max-content', y: 800 }}
        sticky
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <Text strong>Average</Text>
              </Table.Summary.Cell>
        
              {modelKeys.map((code, i) => {
                const mean = averageRow?.[`${code}_mean`] || '';
                const current = parseFloat(mean) || 0;
                const max = modelKeys.reduce((m, k) => {
                  const n = parseFloat(averageRow?.[`${k}_mean`] || 0);
                  return n > m ? n : m;
                }, -Infinity);
                const isMax = current === max;
                let disp = mean;
                if (disp && !disp.startsWith('.')) disp = `.${disp}`;
        
                return (
                  <Table.Summary.Cell key={code} index={i + 1} align="center">
                    <div
                      style={{
                        fontWeight: isMax ? 'bold' : undefined,
                        backgroundColor: isMax ? 'rgba(66,165,245,0.2)' : undefined,
                        borderRadius: 4,
                        padding: '2px 4px'
                      }}
                    >
                      <div>{disp}</div>
                    </div>
                  </Table.Summary.Cell>
                );
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )}        
      />
    </div>
  );
}
